import React from 'react'
import { SingleSelectionSelector } from '@components/Web3/Wallet/SingleSelectionSelector'
import { IconButton, Stack } from '@ui'

import { useMergedAssets } from '../hooks/useMergedAssets'
import { useTradingWallet } from '../hooks/useTradingWallet'
import { MergedTradingAsset } from '../tradingUtils'
import { AssetEntry } from '../ui/AssetEntry'

export function TransferTokenSelector(props: {
    assets: MergedTradingAsset[]
    selectedAsset: MergedTradingAsset | undefined
    invalidateWalletQuery: ReturnType<typeof useTradingWallet>['invalidateWalletQuery']
    fetchLatestWalletData: ReturnType<typeof useTradingWallet>['fetchLatestWalletData']
    setSelectedAsset: (asset: MergedTradingAsset | undefined) => void
}) {
    const {
        assets,
        selectedAsset,
        setSelectedAsset,
        invalidateWalletQuery,
        fetchLatestWalletData,
    } = props

    const { mergeAssets } = useMergedAssets()

    const onAddItem = async (ass: MergedTradingAsset) => {
        await invalidateWalletQuery()
        const data = await fetchLatestWalletData()
        const mergedAssets = mergeAssets(data)
        const latestAsset = mergedAssets.find((asset) => asset.tokenAddress === ass.tokenAddress)
        if (latestAsset) {
            setSelectedAsset(latestAsset)
        }
    }

    const singleSelection = (removeCb: () => void) => {
        const onRemoveClick = () => {
            setSelectedAsset(undefined)
            removeCb()
        }
        if (selectedAsset) {
            return <SelectedAsset asset={selectedAsset} onRemoveClick={onRemoveClick} />
        }
    }

    return (
        <Stack gap data-testid="asset-search">
            <SingleSelectionSelector
                options={assets}
                placeholder="Search Assets"
                keys={['data.address', 'data.symbol', 'data.name']}
                optionRenderer={({ option, selected }) => (
                    <Stack height="x7" marginBottom="-sm">
                        <AssetEntry
                            asset={option}
                            background={{
                                default: 'level2',
                                hover: 'level3',
                            }}
                            showUsdValue={false}
                            onClick={() => onAddItem(option)}
                        />
                    </Stack>
                )}
                getOptionKey={(opt) => opt.tokenAddress + opt.chain}
                singleSelection={(onRemoveClick) => singleSelection(onRemoveClick)}
            />
        </Stack>
    )
}

function SelectedAsset(props: { asset: MergedTradingAsset; onRemoveClick: () => void }) {
    const { asset, onRemoveClick } = props
    return (
        <Stack
            horizontal
            gap="sm"
            paddingX="sm"
            rounded="sm"
            background="level2"
            alignItems="center"
            justifyContent="spaceBetween"
            data-testid="asset-to-transfer"
        >
            <Stack grow>
                <AssetEntry
                    clickable={false}
                    height="x7"
                    background="level2"
                    asset={asset}
                    padding="none"
                    inset="none"
                    showUsdValue={false}
                />
            </Stack>
            <IconButton
                data-testid="remove-asset"
                icon="close"
                tooltip="Unselect"
                onClick={onRemoveClick}
            />
        </Stack>
    )
}
